import Button from '@rio-cloud/rio-uikit/Button';
import Icon, { type IconType } from '@rio-cloud/rio-uikit/Icon';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { parseISO } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppSelector } from '../../../../configuration/setup/store';
import { useMarketplaceSidebarTarget } from '../../../marketplaceSidebarContext';
import { mapContractPeriod } from '../../api/periods';
import type { ApiProduct } from '../../api/product/product.types';
import { isBookableProduct, isMultiLevelProduct, type Product, type ProductLevel } from '../../checkout/redux/types';
import { DescriptionOfServiceContainer } from '../../common/descriptionOfService/DescriptionOfServiceContainer';
import { getEstimatedPrice, getFullTermPrice } from '../../common/price/priceUtil';
import { ProductType } from '../../common/product/product';
import { getIsRenewDialogOpen } from '../../common/termedLevel/redux/termedLevelRenewal.redux';
import { isTermedLevelSelectable } from '../../common/termedLevel/termedLevelSelectionValidationService';
import type { TermedLevel } from '../../common/termedLevel/types';
import { TermsAndConditionsContainer } from '../../common/termsAndConditions/TermsAndConditionsContainer';
import { formatDurationUnit } from '../../common/utils/formatDurationUnit';
import { isManNowProduct } from '../../config/manNowProducts';
import { ContractTableRowRenewalText } from '../contracts/ContractTableRowRenewalText';
import { resolveContractDocuments } from '../contracts/contractDocumentResolution';
import { mapResourceTypeToProductType } from '../contracts/mapResourceTypeToProductType';
import { BookingOverviewContractState } from './BookingOverviewContractTable';
import { BookingOverviewRemainingDays } from './BookingOverviewRemainingDays';
import { getBookingOverviewStatus } from './bookingOverviewStatus';

interface Props {
    contract?: TermedLevel;
    product?: Product;
    productDetails?: ApiProduct;
    assetType?: string;
    canConfigureRenewal: boolean;
    onConfigureRenewal: (contract: TermedLevel) => void;
    onClose: () => void;
}

const message = (id: string) => <FormattedMessage id={id} />;

type ProductLevelWithPrice = Pick<ProductLevel, 'price' | 'currency' | 'contractPeriod'>;

function getProductLevel(
    productDetails: ApiProduct | undefined,
    product: Product | undefined,
    level: string
): ProductLevelWithPrice | undefined {
    const ratePlan = productDetails?.rate_plans?.find(ratePlan => ratePlan.level === level);
    if (ratePlan) {
        return {
            price: ratePlan.price,
            currency: ratePlan.currency,
            contractPeriod: mapContractPeriod(ratePlan.contract_period),
        };
    }

    if (isMultiLevelProduct(product)) {
        return product.levels.find(productLevel => productLevel.level === level);
    }

    return isBookableProduct(product) && product.level === level ? product : undefined;
}

function getAssetIcon(assetType: string | undefined, productType: ProductType): IconType {
    if (productType === ProductType.USER_BASED) {
        return 'user';
    }

    switch (assetType?.toLowerCase()) {
        case 'bus':
            return 'bus';
        case 'van':
            return 'van';
        case 'trailer':
            return 'trailer';
        default:
            return 'truck';
    }
}

function Detail({
    label,
    value,
    compact = false,
}: {
    label: React.ReactNode;
    value?: React.ReactNode;
    compact?: boolean;
}) {
    return (
        <div className='word-break-all min-width-0 gap-0 display-flex flex-column bg-white shadow-subtle rounded padding-x-15 padding-y-10 flex-1-1-0'>
            <span className='text-color-dark text-size-12 margin-top--5'>{label}</span>
            <div className={`text-bold ${compact ? 'text-size-14' : 'text-size-16'}`}>{value ?? '–'}</div>
        </div>
    );
}

function SectionTitle({ iconName, children }: { iconName: IconType; children: React.ReactNode }) {
    return (
        <div className='text-bold text-color-darker display-flex align-items-center gap-5 margin-top-10 margin-bottom-5'>
            <span aria-hidden>
                <Icon iconName={iconName} size='16' />
            </span>
            {children}
        </div>
    );
}

function Costs({ productLevel, contract }: { productLevel?: ProductLevelWithPrice; contract: TermedLevel }) {
    const intl = useIntl();
    if (!productLevel) {
        return <div className='bg-lightest rounded padding-10'>–</div>;
    }

    const { price, currency } = productLevel;
    const formatCost = (value: number) =>
        `${intl.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
    const period =
        contract.periodLength && contract.periodUnit
            ? { length: contract.periodLength, unit: contract.periodUnit }
            : productLevel.contractPeriod;

    return (
        <div className='bg-lightest rounded padding-x-15 padding-y-10 display-flex flex-column'>
            <div className='display-flex justify-content-between gap-15'>
                <span>{message('intl-msg:marketplace.contractDetails.cost.daily')}</span>
                <strong className='text-size-16'>{formatCost(price)}</strong>
            </div>
            <div className='display-flex justify-content-between gap-15'>
                <span>{message('intl-msg:marketplace.contractDetails.cost.monthly')}</span>
                <strong className='text-size-16'>{formatCost(getEstimatedPrice(price))}</strong>
            </div>
            <div className='display-flex justify-content-between gap-15'>
                <span>{message('intl-msg:marketplace.contractDetails.cost.yearly')}</span>
                <strong className='text-size-16'>{formatCost(price * 365)}</strong>
            </div>
            {period && (
                <div className='display-flex justify-content-between gap-15'>
                    <span>{message('intl-msg:marketplace.contractDetails.cost.fullTerm')}</span>
                    <strong className='text-size-16'>{formatCost(getFullTermPrice(price, period))}</strong>
                </div>
            )}
        </div>
    );
}

export function BookingOverviewContractSidebar({
    contract,
    product,
    productDetails,
    assetType,
    canConfigureRenewal,
    onConfigureRenewal,
    onClose,
}: Props) {
    const intl = useIntl();
    const bookingOverviewSidebarTarget = useMarketplaceSidebarTarget();
    const [isShowingTerms, setIsShowingTerms] = useState(false);
    const [isShowingDescription, setIsShowingDescription] = useState(false);
    const renewalEditButton = useRef<HTMLButtonElement>(null);
    const isRenewDialogOpen = useAppSelector(getIsRenewDialogOpen);

    useEffect(() => {
        if (isRenewDialogOpen) {
            return () => renewalEditButton.current?.focus();
        }
    }, [isRenewDialogOpen]);

    const productLevel = contract ? getProductLevel(productDetails, product, contract.level) : undefined;
    const productType = product?.productType ?? mapResourceTypeToProductType(contract?.resourceType ?? 'asset');
    const documents = contract ? resolveContractDocuments(contract) : undefined;

    if (!bookingOverviewSidebarTarget) {
        return null;
    }

    return createPortal(
        <Sidebar
            closed={!contract}
            onClose={onClose}
            position={Sidebar.RIGHT}
            width={450}
            switchModeBreakpoint={900}
            className='BookingOverviewSidebar max-width-100vw'
            title={message('intl-msg:marketplace.contractDetails.bookingDetails')}
            titleClassName='padding-left-5'
            bodyClassName='padding-x-15 padding-bottom-15'
        >
            {contract && (
                <>
                    {documents?.canOpenTos && isShowingTerms && (
                        <TermsAndConditionsContainer
                            serviceSku={documents.productId}
                            level={contract.level}
                            documentVersion={documents.tosVersion}
                            show={isShowingTerms}
                            onClose={() => setIsShowingTerms(false)}
                        />
                    )}
                    {documents?.canOpenDos && isShowingDescription && (
                        <DescriptionOfServiceContainer
                            serviceSku={documents.productId}
                            level={contract.level}
                            documentVersion={documents.dosVersion}
                            show={isShowingDescription}
                            onClose={() => setIsShowingDescription(false)}
                        />
                    )}

                    <section className='bg-lightest rounded padding-10 margin-0'>
                        <div className='bg-white shadow-subtle rounded padding-x-15 padding-y-10 display-flex align-items-center justify-content-between gap-15'>
                            <div className='word-break-all min-width-0 display-flex flex-column'>
                                <span className='text-color-dark text-size-12 margin-top--5'>
                                    {message('intl-msg:marketplace.contractDetails.vehicle')}
                                </span>
                                <strong>{contract.resourceName || contract.resourceId}</strong>
                            </div>
                            <span aria-hidden>
                                <Icon iconName={getAssetIcon(assetType, productType)} size='h2' />
                            </span>
                        </div>
                        <div className='display-flex gap-10 margin-top-10'>
                            <Detail
                                compact
                                label={message('intl-msg:marketplace.contractDetails.product')}
                                value={contract.productName}
                            />
                            <Detail
                                label={message('intl-msg:marketplace.contractDetails.variant')}
                                value={
                                    contract.variantShortName?.trim() ? (
                                        <span className='label label-primary label-filled label-condensed white-space-normal'>
                                            {contract.variantShortName}
                                        </span>
                                    ) : undefined
                                }
                            />
                        </div>
                        {(contract.resourceLicensePlate || contract.resourceVin) && (
                            <div className='display-flex gap-10 margin-top-10'>
                                {contract.resourceLicensePlate && (
                                    <Detail
                                        label={message('intl-msg:marketplace.contractDetails.licensePlate')}
                                        value={contract.resourceLicensePlate}
                                    />
                                )}
                                {contract.resourceVin && <Detail label='VIN' value={contract.resourceVin} />}
                            </div>
                        )}
                        {canConfigureRenewal &&
                            productType !== ProductType.USER_BASED &&
                            getBookingOverviewStatus(contract) !== 'UNASSIGNED' && (
                                <Button
                                    as='a'
                                    bsStyle='primary'
                                    variant='link-inline'
                                    iconName='new-window'
                                    iconRight
                                    className='padding-0 margin-top-10'
                                    href={`https://administration.assets.rio.cloud/#/assets/${encodeURIComponent(contract.resourceId)}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {message('intl-msg:marketplace.contractDetails.openFleetAdministration')}
                                </Button>
                            )}
                    </section>

                    <section className='margin-0 padding-top-0'>
                        <SectionTitle iconName='signature'>
                            {message('intl-msg:marketplace.contractDetails.contract')}
                        </SectionTitle>
                        <div className='bg-lightest rounded padding-10'>
                            <div className='display-flex gap-10'>
                                <Detail
                                    label={message('intl-msg:marketplace.bookings.overview.table.status')}
                                    value={<BookingOverviewContractState contract={contract} showExplanation />}
                                />
                                <Detail
                                    label={message('intl-msg:marketplace.bookings.overview.table.renewsTo')}
                                    value={
                                        <div className='display-flex align-items-center gap-5'>
                                            <span className='min-width-0'>
                                                <ContractTableRowRenewalText contract={contract} />
                                            </span>
                                            <Button
                                                ref={renewalEditButton}
                                                bsStyle='primary'
                                                variant='link'
                                                bsSize='xs'
                                                iconName='pencil-square'
                                                iconOnly
                                                aria-label={intl.formatMessage({
                                                    id: 'intl-msg:marketplace.contractDetails.editRenewal',
                                                })}
                                                tooltip={message('intl-msg:marketplace.contractDetails.editRenewal')}
                                                disabled={
                                                    !canConfigureRenewal ||
                                                    !isTermedLevelSelectable(contract) ||
                                                    isManNowProduct(contract.productId)
                                                }
                                                onClick={() => onConfigureRenewal(contract)}
                                            />
                                        </div>
                                    }
                                />
                            </div>
                            <div className='display-flex gap-10 margin-top-10'>
                                <Detail
                                    label={message('intl-msg:marketplace.bookings.overview.table.period')}
                                    value={
                                        contract.periodLength && contract.periodUnit
                                            ? `${contract.periodLength} ${formatDurationUnit(
                                                  contract.periodUnit,
                                                  intl,
                                                  contract.periodLength
                                              )}`
                                            : undefined
                                    }
                                />
                                <Detail
                                    label={message('intl-msg:marketplace.bookings.overview.table.daysLeft')}
                                    value={<BookingOverviewRemainingDays contract={contract} />}
                                />
                            </div>
                            <div className='display-flex gap-10 margin-top-10'>
                                <Detail
                                    label={message('intl-msg:marketplace.contractDetails.start')}
                                    value={
                                        contract.startsAt &&
                                        intl.formatDate(parseISO(contract.startsAt), {
                                            day: 'numeric',
                                            month: 'numeric',
                                            year: 'numeric',
                                        })
                                    }
                                />
                                <Detail
                                    label={message('intl-msg:marketplace.contractDetails.end')}
                                    value={
                                        contract.endsAt
                                            ? intl.formatDate(parseISO(contract.endsAt), {
                                                  day: 'numeric',
                                                  month: 'numeric',
                                                  year: 'numeric',
                                              })
                                            : contract.startsAt
                                              ? '∞'
                                              : undefined
                                    }
                                />
                            </div>
                        </div>
                    </section>

                    <section className='margin-0 padding-top-0'>
                        <SectionTitle iconName='currency-euro'>
                            {message('intl-msg:marketplace.contractDetails.estimatedCosts')}
                        </SectionTitle>
                        <Costs productLevel={productLevel} contract={contract} />
                    </section>

                    <section className='margin-0 padding-top-0'>
                        <SectionTitle iconName='pdf-file'>
                            {message('intl-msg:marketplace.contractDetails.documents')}
                        </SectionTitle>
                        <div className='bg-lightest rounded padding-x-15 padding-y-15 display-flex flex-column align-items-start gap-5'>
                            <Button
                                bsStyle='primary'
                                variant='link-inline'
                                iconName='link'
                                disabled={!documents?.canOpenDos}
                                onClick={() => setIsShowingDescription(true)}
                            >
                                {message('intl-msg:marketplace.contractDetails.serviceDescription')}
                            </Button>
                            <Button
                                bsStyle='primary'
                                variant='link-inline'
                                iconName='link'
                                disabled={!documents?.canOpenTos}
                                onClick={() => setIsShowingTerms(true)}
                            >
                                {message('intl-msg:marketplace.contractDetails.terms')}
                            </Button>
                        </div>
                    </section>
                </>
            )}
        </Sidebar>,
        bookingOverviewSidebarTarget
    );
}
